import { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { API } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { GripVertical, Plus, Trash2, ExternalLink, Save, Link as LinkIcon, Edit2, X, Check, Download, Upload, Palette, FolderPlus, Building2, Eye, EyeOff, Link2, BarChart3, Phone, Users, DollarSign, TrendingUp, FileText, Calendar, Star, Target, Bell } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { ManagementToolsSettings } from './ManagementToolsSettings';
import { NotificationBarSettings } from './NotificationBarSettings';

const CRM_PRESETS = [
  { id: 'link',     label: 'Link',      Icon: Link2,       color: '#6b7280' },
  { id: 'chart',    label: 'Report',    Icon: BarChart3,   color: '#2563eb' },
  { id: 'phone',    label: 'Calls',     Icon: Phone,       color: '#16a34a' },
  { id: 'users',    label: 'Contacts',  Icon: Users,       color: '#7c3aed' },
  { id: 'dollar',   label: 'Sales',     Icon: DollarSign,  color: '#ea580c' },
  { id: 'trending', label: 'Growth',    Icon: TrendingUp,  color: '#0891b2' },
  { id: 'file',     label: 'Docs',      Icon: FileText,    color: '#d97706' },
  { id: 'calendar', label: 'Schedule',  Icon: Calendar,    color: '#dc2626' },
  { id: 'star',     label: 'Favourite', Icon: Star,        color: '#ca8a04' },
  { id: 'target',   label: 'Targets',   Icon: Target,      color: '#be185d' },
];

const renderCrmIcon = (iconUrl, size = 'w-3.5 h-3.5') => {
  if (!iconUrl) return <Link2 className={`${size} text-gray-400`} />;
  if (iconUrl.startsWith('data:')) return <img src={iconUrl} alt="" className="w-4 h-4 object-contain rounded" />;
  if (iconUrl.startsWith('preset:')) {
    const p = CRM_PRESETS.find(x => x.id === iconUrl.slice(7));
    if (p) return <p.Icon className={size} style={{ color: p.color }} />;
  }
  return <img src={iconUrl} alt="" className="w-4 h-4 object-contain" onError={e => { e.target.style.display = 'none'; }} />;
};

const DashboardManager = ({ user }) => {
  const [apps, setApps] = useState([]);
  const [activeTab, setActiveTab] = useState('tools');
  const [links, setLinks] = useState([]);
  const [linkGroups, setLinkGroups] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchVisibilityMode, setBranchVisibilityMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [linksLoading, setLinksLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddLinkModal, setShowAddLinkModal] = useState(false);
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [editingLinkId, setEditingLinkId] = useState(null);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [newLink, setNewLink] = useState({ name: '', url: '', group_id: 'default' });
  const [editLinkData, setEditLinkData] = useState({ name: '', url: '' });
  const [newGroup, setNewGroup] = useState({ name: '', color: '#8b5cf6' });
  const [editGroupData, setEditGroupData] = useState({ name: '', color: '' });
  const [newApp, setNewApp] = useState({
    name: '',
    description: '',
    route: '',
    icon_type: 'ExternalLink',
    color: '#ee7203',
    is_external: true,
  });
  
  const fileInputRef = useRef(null);

  const enconOrange = '#ee7203';
  const enconGrey = '#9d9d9c';

  // CRM Links state
  const [crmLinks, setCrmLinks] = useState([]);
  const [newCrmLink, setNewCrmLink] = useState({ name: '', url: '', icon_url: '' });
  const [crmLinkAdding, setCrmLinkAdding] = useState(false);
  const iconFileRef = useRef(null);

  const handleIconUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setNewCrmLink(p => ({ ...p, icon_url: ev.target.result }));
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  useEffect(() => {
    loadApps();
    loadLinks();
    loadLinkGroups();
    loadBranches();
    loadCrmLinks();
  }, []);

  const loadCrmLinks = async () => {
    try {
      const res = await api.get(`${API}/crm-links`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setCrmLinks(res.data?.links || []);
    } catch (_) {}
  };

  const addCrmLink = async () => {
    if (!newCrmLink.name.trim() || !newCrmLink.url.trim()) { toast.error('Name and URL are required'); return; }
    setCrmLinkAdding(true);
    try {
      const res = await api.post(`${API}/crm-links`, newCrmLink, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setCrmLinks(prev => [...prev, res.data.link]);
      setNewCrmLink({ name: '', url: '', icon_url: '' });
      toast.success('CRM link added');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add link'); }
    finally { setCrmLinkAdding(false); }
  };

  const deleteCrmLink = async (id) => {
    if (!window.confirm('Remove this CRM link?')) return;
    try {
      await api.delete(`${API}/crm-links/${id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setCrmLinks(prev => prev.filter(l => l.id !== id));
      toast.success('Removed');
    } catch (e) { toast.error('Failed to remove'); }
  };

  const loadBranches = async () => {
    try {
      const response = await api.get(`${API}/branches`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setBranches(response.data.branches || []);
      // Default to first branch for visibility editing
      if (response.data.branches?.length > 0) {
        setSelectedBranch(response.data.branches[0].id);
      }
    } catch (error) {
      console.error('Error loading branches:', error);
    }
  };

  // Load ALL links for admin (unfiltered)
  const loadAllLinks = async () => {
    try {
      const response = await api.get(`${API}/quick-links/all`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setLinks(response.data);
    } catch (error) {
      console.error('Error loading all links:', error);
      // Fall back to regular endpoint
      loadLinks();
    }
  };

  // Load ALL groups for admin (unfiltered)
  const loadAllLinkGroups = async () => {
    try {
      const response = await api.get(`${API}/quick-link-groups/all`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setLinkGroups(response.data);
    } catch (error) {
      console.error('Error loading all groups:', error);
      // Fall back to regular endpoint
      loadLinkGroups();
    }
  };

  // Toggle branch visibility for a link
  const toggleLinkBranchVisibility = async (linkId, branchId) => {
    const link = links.find(l => l.id === linkId);
    if (!link) return;
    
    const currentVisibility = link.branch_visibility || {};
    const isCurrentlyVisible = currentVisibility[branchId] !== false; // Default to true
    
    try {
      await api.put(`${API}/quick-links/${linkId}/branch-visibility`, {
        branch_id: branchId,
        visible: !isCurrentlyVisible
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Update local state
      setLinks(prev => prev.map(l => {
        if (l.id === linkId) {
          return {
            ...l,
            branch_visibility: {
              ...l.branch_visibility,
              [branchId]: !isCurrentlyVisible
            }
          };
        }
        return l;
      }));
      
      toast.success(`Link ${!isCurrentlyVisible ? 'shown' : 'hidden'} for ${branchId}`);
    } catch (error) {
      console.error('Error updating link visibility:', error);
      toast.error('Failed to update visibility');
    }
  };

  // Toggle branch visibility for a group (pill)
  const toggleGroupBranchVisibility = async (groupId, branchId) => {
    const group = linkGroups.find(g => g.id === groupId);
    if (!group) return;
    
    const currentVisibility = group.branch_visibility || {};
    const isCurrentlyVisible = currentVisibility[branchId] !== false; // Default to true
    
    try {
      await api.put(`${API}/quick-link-groups/${groupId}/branch-visibility`, {
        branch_id: branchId,
        visible: !isCurrentlyVisible
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Update local state
      setLinkGroups(prev => prev.map(g => {
        if (g.id === groupId) {
          return {
            ...g,
            branch_visibility: {
              ...g.branch_visibility,
              [branchId]: !isCurrentlyVisible
            }
          };
        }
        return g;
      }));
      
      toast.success(`Group ${!isCurrentlyVisible ? 'shown' : 'hidden'} for ${branchId}`);
    } catch (error) {
      console.error('Error updating group visibility:', error);
      toast.error('Failed to update visibility');
    }
  };

  // Check if item is visible for selected branch
  const isVisibleForBranch = (item, branchId) => {
    if (!item.branch_visibility) return true; // Default visible
    return item.branch_visibility[branchId] !== false;
  };

  // When entering branch visibility mode, load all items
  useEffect(() => {
    if (branchVisibilityMode) {
      loadAllLinks();
      loadAllLinkGroups();
    }
  }, [branchVisibilityMode]);

  const loadApps = async () => {
    try {
      const response = await api.get(`${API}/hub-apps`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setApps(response.data);
    } catch (error) {
      console.error('Error loading hub apps:', error);
      toast.error('Failed to load hub apps');
    } finally {
      setLoading(false);
    }
  };

  const loadLinks = async () => {
    try {
      const response = await api.get(`${API}/quick-links`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setLinks(response.data);
    } catch (error) {
      console.error('Error loading links:', error);
    } finally {
      setLinksLoading(false);
    }
  };

  const loadLinkGroups = async () => {
    try {
      const response = await api.get(`${API}/quick-link-groups`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      // Ensure groups are sorted by order
      const sortedGroups = (response.data || []).sort((a, b) => (a.order || 0) - (b.order || 0));
      setLinkGroups(sortedGroups);
    } catch (error) {
      console.error('Error loading link groups:', error);
    }
  };

  // Get links for a specific group
  const getLinksForGroup = (groupId) => {
    return links.filter(link => (link.group_id || 'default') === groupId).sort((a, b) => a.order - b.order);
  };

  // Apps drag and drop
  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(apps);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index + 1
    }));

    setApps(updatedItems);

    try {
      await api.post(`${API}/hub-apps/reorder`,
        updatedItems.map(app => ({ id: app.id, order: app.order })),
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Order updated successfully');
    } catch (error) {
      console.error('Error reordering apps:', error);
      toast.error('Failed to save new order');
      loadApps();
    }
  };

  // Links drag and drop within a group
  const handleLinksDragEnd = async (result, groupId) => {
    if (!result.destination) return;

    const groupLinks = getLinksForGroup(groupId);
    const items = Array.from(groupLinks);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index + 1
    }));

    // Update local state
    setLinks(prev => {
      const otherLinks = prev.filter(l => (l.group_id || 'default') !== groupId);
      return [...otherLinks, ...updatedItems];
    });

    try {
      await api.put(`${API}/quick-links/reorder`,
        { links: updatedItems.map(link => ({ id: link.id, order: link.order, group_id: groupId })) },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Links order updated');
    } catch (error) {
      console.error('Error reordering links:', error);
      toast.error('Failed to save links order');
      loadLinks();
    }
  };

  // Groups drag and drop
  const handleGroupsDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(linkGroups);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index + 1
    }));

    setLinkGroups(updatedItems);

    try {
      await api.put(`${API}/quick-link-groups/reorder`,
        { groups: updatedItems.map(g => ({ id: g.id, order: g.order })) },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Groups order updated');
    } catch (error) {
      console.error('Error reordering groups:', error);
      toast.error('Failed to save groups order');
      loadLinkGroups();
    }
  };

  const handleAddLink = async () => {
    if (!newLink.name || !newLink.url) {
      toast.error('Please fill in both name and URL');
      return;
    }

    try {
      await api.post(`${API}/quick-links`, {
        name: newLink.name,
        url: newLink.url,
        group_id: newLink.group_id || 'default'
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      toast.success('Link added successfully');
      setShowAddLinkModal(false);
      setNewLink({ name: '', url: '', group_id: 'default' });
      loadLinks();
    } catch (error) {
      console.error('Error adding link:', error);
      toast.error('Failed to add link');
    }
  };

  const handleUpdateLink = async (linkId) => {
    if (!editLinkData.name || !editLinkData.url) {
      toast.error('Please fill in both name and URL');
      return;
    }

    try {
      await api.put(`${API}/quick-links/${linkId}`, {
        name: editLinkData.name,
        url: editLinkData.url
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      toast.success('Link updated successfully');
      setEditingLinkId(null);
      loadLinks();
    } catch (error) {
      console.error('Error updating link:', error);
      toast.error('Failed to update link');
    }
  };

  const handleDeleteLink = async (linkId) => {
    if (!confirm('Are you sure you want to delete this link?')) return;

    try {
      await api.delete(`${API}/quick-links/${linkId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Link deleted successfully');
      loadLinks();
    } catch (error) {
      console.error('Error deleting link:', error);
      toast.error('Failed to delete link');
    }
  };

  const handleAddGroup = async () => {
    if (!newGroup.name) {
      toast.error('Please enter a group name');
      return;
    }

    try {
      await api.post(`${API}/quick-link-groups`, {
        name: newGroup.name,
        color: newGroup.color
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      toast.success('Group created successfully');
      setShowAddGroupModal(false);
      setNewGroup({ name: '', color: '#8b5cf6' });
      loadLinkGroups();
    } catch (error) {
      console.error('Error creating group:', error);
      toast.error('Failed to create group');
    }
  };

  const handleUpdateGroup = async (groupId) => {
    try {
      await api.put(`${API}/quick-link-groups/${groupId}`, {
        name: editGroupData.name,
        color: editGroupData.color
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      toast.success('Group updated');
      setEditingGroupId(null);
      loadLinkGroups();
    } catch (error) {
      console.error('Error updating group:', error);
      toast.error('Failed to update group');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (groupId === 'default') {
      toast.error('Cannot delete the default group');
      return;
    }
    if (!confirm('Are you sure? This will delete all links in this group.')) return;

    try {
      await api.delete(`${API}/quick-link-groups/${groupId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Group deleted');
      loadLinkGroups();
      loadLinks();
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error('Failed to delete group');
    }
  };

  const startEditLink = (link) => {
    setEditingLinkId(link.id);
    setEditLinkData({ name: link.name, url: link.url });
  };

  const startEditGroup = (group) => {
    setEditingGroupId(group.id);
    setEditGroupData({ name: group.name, color: group.color });
  };

  // Export links configuration
  const handleExport = async () => {
    try {
      const response = await api.get(`${API}/quick-links/export`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quick-links-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Links exported successfully');
    } catch (error) {
      console.error('Error exporting links:', error);
      toast.error('Failed to export links');
    }
  };

  // Import links configuration
  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const importData = JSON.parse(text);

      if (!confirm('This will replace all existing links and groups. Continue?')) {
        return;
      }

      await api.post(`${API}/quick-links/import`, importData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      toast.success('Links imported successfully');
      loadLinkGroups();
      loadLinks();
    } catch (error) {
      console.error('Error importing links:', error);
      toast.error('Failed to import links - check file format');
    }

    event.target.value = '';
  };

  const handleAddApp = async () => {
    if (!newApp.name || !newApp.route) {
      toast.error('Please fill in name and URL');
      return;
    }

    try {
      const appData = {
        ...newApp,
        order: apps.length + 1,
        is_default: false,
      };

      await api.post(`${API}/hub-apps`, appData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      toast.success('App added successfully');
      setShowAddModal(false);
      setNewApp({
        name: '',
        description: '',
        route: '',
        icon_type: 'ExternalLink',
        color: '#ee7203',
        is_external: true,
      });
      loadApps();
    } catch (error) {
      console.error('Error adding app:', error);
      toast.error('Failed to add app');
    }
  };

  const handleDeleteApp = async (appId, isDefault) => {
    if (isDefault) {
      toast.error('Cannot delete default apps');
      return;
    }

    if (!confirm('Are you sure you want to delete this app?')) return;

    try {
      await api.delete(`${API}/hub-apps/${appId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('App deleted successfully');
      loadApps();
    } catch (error) {
      console.error('Error deleting app:', error);
      toast.error('Failed to delete app');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  const TABS = [
    { key: 'tools',  label: 'Manage Tools' },
    { key: 'crm',    label: 'CRM Links' },
    { key: 'quick',  label: 'Quick Links' },
    ...(user?.role === 'super_admin' ? [{ key: 'notif', label: 'Notification Bar' }] : []),
  ];

  return (
    <div className="space-y-4">
      {/* Tab strip */}
      <div className="flex gap-1 border-b" style={{ borderColor: '#e5e7eb' }}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition-colors ${
              activeTab === tab.key
                ? 'text-white'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            style={activeTab === tab.key ? { backgroundColor: '#ee7203', borderColor: '#ee7203' } : {}}
            data-testid={`dashboard-tab-${tab.key}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'tools' && <ManagementToolsSettings />}

      {activeTab === 'crm' && (
        <div className="space-y-4">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2" style={{ color: '#1d1d1b' }}>
              <Link2 className="w-6 h-6" style={{ color: enconOrange }} />
              CRM Links Manager
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Links shown in the Manage CRM dropdown — quick access to CRM reports or pages.
            </p>
          </div>

        {/* Add form */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Add a CRM Link</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Name</label>
              <input
                type="text"
                value={newCrmLink.name}
                onChange={e => setNewCrmLink(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Call Activity Report"
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-400"
                data-testid="crm-link-name-input"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">URL</label>
              <input
                type="url"
                value={newCrmLink.url}
                onChange={e => setNewCrmLink(p => ({ ...p, url: e.target.value }))}
                placeholder="https://crm.zoho.com/..."
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-400"
                data-testid="crm-link-url-input"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Icon <span className="text-gray-400 font-normal">(optional)</span></label>
              <div className="flex flex-wrap items-center gap-1.5">
                {CRM_PRESETS.map(p => {
                  const selected = newCrmLink.icon_url === `preset:${p.id}`;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      title={p.label}
                      onClick={() => setNewCrmLink(prev => ({ ...prev, icon_url: selected ? '' : `preset:${p.id}` }))}
                      className={`w-7 h-7 rounded-lg flex items-center justify-center border-2 transition-all ${selected ? 'border-orange-400 bg-orange-50' : 'border-gray-200 bg-gray-50 hover:bg-gray-100'}`}
                      data-testid={`crm-preset-${p.id}`}
                    >
                      <p.Icon className="w-3.5 h-3.5" style={{ color: selected ? enconOrange : p.color }} />
                    </button>
                  );
                })}
                {/* Custom image upload */}
                <button
                  type="button"
                  onClick={() => iconFileRef.current?.click()}
                  title="Upload custom image"
                  className={`w-7 h-7 rounded-lg flex items-center justify-center border-2 transition-all overflow-hidden ${newCrmLink.icon_url?.startsWith('data:') ? 'border-orange-400' : 'border-dashed border-gray-300 hover:border-gray-400 bg-gray-50'}`}
                  data-testid="crm-icon-upload-btn"
                >
                  {newCrmLink.icon_url?.startsWith('data:')
                    ? <img src={newCrmLink.icon_url} alt="" className="w-5 h-5 object-contain" />
                    : <Upload className="w-3 h-3 text-gray-400" />}
                </button>
                {newCrmLink.icon_url && (
                  <button
                    type="button"
                    onClick={() => setNewCrmLink(p => ({ ...p, icon_url: '' }))}
                    className="w-5 h-5 rounded-full bg-gray-200 hover:bg-red-100 flex items-center justify-center"
                    title="Clear icon"
                  >
                    <X className="w-3 h-3 text-gray-500" />
                  </button>
                )}
              </div>
              <input ref={iconFileRef} type="file" accept="image/*" className="hidden" onChange={handleIconUpload} data-testid="crm-icon-file-input" />
            </div>
          </div>
          <Button
            onClick={addCrmLink}
            disabled={crmLinkAdding}
            className="flex items-center gap-2 text-white"
            style={{ backgroundColor: enconOrange }}
            data-testid="crm-link-add-btn"
          >
            <Plus className="w-4 h-4" />
            {crmLinkAdding ? 'Adding…' : 'Add CRM Link'}
          </Button>
        </div>

        {/* Existing links */}
        {crmLinks.length === 0 ? (
          <p className="text-sm text-gray-400 italic">No CRM links yet. Add one above to display it in the Manage CRM dropdown.</p>
        ) : (
          <div className="space-y-2">
            {crmLinks.map(link => (
                <div key={link.id} className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3" data-testid={`crm-link-row-${link.id}`}>
                  <div className="w-7 h-7 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {renderCrmIcon(link.icon_url)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900">{link.name}</div>
                    <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-blue-500 hover:underline truncate block">{link.url}</a>
                  </div>
                  <button
                    onClick={() => deleteCrmLink(link.id)}
                    className="text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg p-1.5 transition-colors"
                    title="Remove"
                    data-testid={`crm-link-delete-${link.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
            ))}
          </div>
        )}
      </div>
      )}

      {activeTab === 'quick' && (
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2" style={{ color: '#1d1d1b' }}>
              <LinkIcon className="w-6 h-6" style={{ color: '#8b5cf6' }} />
              Quick Links Manager
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Manage quick links shown on the Home page and Dashboard panel. Groups are separated by ||
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* Branch Visibility Toggle */}
            <Button
              variant={branchVisibilityMode ? "default" : "outline"}
              onClick={() => setBranchVisibilityMode(!branchVisibilityMode)}
              className={`flex items-center gap-2 ${branchVisibilityMode ? 'bg-purple-600 hover:bg-purple-700' : 'border-purple-300 text-purple-600 hover:bg-purple-50'}`}
            >
              <Building2 className="w-4 h-4" />
              Branch Visibility
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              accept=".json"
              onChange={handleImport}
              className="hidden"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              Import
            </Button>
            <Button
              variant="outline"
              onClick={handleExport}
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export
            </Button>
            <Button
              onClick={() => setShowAddGroupModal(true)}
              variant="outline"
              className="flex items-center gap-2 border-purple-300 text-purple-600 hover:bg-purple-50"
            >
              <FolderPlus className="w-4 h-4" />
              Add Group
            </Button>
            <Button
              onClick={() => setShowAddLinkModal(true)}
              style={{ backgroundColor: '#8b5cf6' }}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Link
            </Button>
          </div>
        </div>

        {/* Branch Visibility Controls */}
        {branchVisibilityMode && (
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-purple-600" />
                <h3 className="font-semibold text-purple-800">Branch Visibility Settings</h3>
              </div>
              <Select value={selectedBranch || ''} onValueChange={setSelectedBranch}>
                <SelectTrigger className="w-48 bg-white">
                  <SelectValue placeholder="Select branch" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map(branch => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.display_name || branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <p className="text-sm text-purple-700">
              Toggle which links and groups are visible for <strong>{branches.find(b => b.id === selectedBranch)?.display_name || selectedBranch}</strong>. 
              Hidden items won't appear for users in this branch.
            </p>

            {/* Groups visibility */}
            <div>
              <h4 className="text-sm font-medium text-purple-800 mb-2">Groups (Pills)</h4>
              <div className="flex flex-wrap gap-2">
                {linkGroups.map(group => {
                  const isVisible = isVisibleForBranch(group, selectedBranch);
                  return (
                    <button
                      key={group.id}
                      onClick={() => toggleGroupBranchVisibility(group.id, selectedBranch)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-2 transition-all ${
                        isVisible 
                          ? 'bg-white border-2 shadow-sm' 
                          : 'bg-gray-100 border-2 border-dashed border-gray-300 opacity-60'
                      }`}
                      style={{ borderColor: isVisible ? group.color : undefined }}
                    >
                      {isVisible ? <Eye className="w-3 h-3 text-green-600" /> : <EyeOff className="w-3 h-3 text-gray-400" />}
                      <span style={{ color: isVisible ? group.color : '#9ca3af' }}>{group.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Links visibility per group */}
            <div>
              <h4 className="text-sm font-medium text-purple-800 mb-2">Links</h4>
              <div className="space-y-3">
                {linkGroups.map(group => {
                  const groupLinks = links.filter(l => (l.group_id || 'default') === group.id);
                  if (groupLinks.length === 0) return null;
                  
                  return (
                    <div key={group.id} className="bg-white rounded-lg p-3 border">
                      <div className="text-xs font-medium mb-2" style={{ color: group.color }}>{group.name}</div>
                      <div className="flex flex-wrap gap-2">
                        {groupLinks.map(link => {
                          const isVisible = isVisibleForBranch(link, selectedBranch);
                          return (
                            <button
                              key={link.id}
                              onClick={() => toggleLinkBranchVisibility(link.id, selectedBranch)}
                              className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1.5 transition-all ${
                                isVisible 
                                  ? 'bg-purple-100 text-purple-700' 
                                  : 'bg-gray-100 text-gray-400 line-through'
                              }`}
                            >
                              {isVisible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                              {link.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Links Preview */}
        <div className="bg-gray-50 rounded-lg p-4 border">
          <div className="text-xs text-gray-500 mb-2">Preview:</div>
          <div className="flex flex-wrap items-center gap-1">
            {linkGroups.map((group, groupIndex) => {
              const groupLinks = getLinksForGroup(group.id);
              if (groupLinks.length === 0) return null;
              
              return (
                <span key={group.id} className="flex items-center">
                  {groupIndex > 0 && linkGroups.slice(0, groupIndex).some(g => getLinksForGroup(g.id).length > 0) && (
                    <span className="mx-3 text-gray-400 font-bold">||</span>
                  )}
                  {groupLinks.map((link, linkIndex) => (
                    <span key={link.id} className="flex items-center">
                      <span 
                        className="text-sm font-medium"
                        style={{ color: group.color }}
                      >
                        {link.name}
                      </span>
                      {linkIndex < groupLinks.length - 1 && (
                        <span className="mx-2 text-gray-300">|</span>
                      )}
                    </span>
                  ))}
                </span>
              );
            })}
            {links.length === 0 && (
              <span className="text-gray-400 text-sm italic">No links added yet</span>
            )}
          </div>
        </div>

        {/* Groups and Links with Drag and Drop */}
        {linksLoading ? (
          <div className="text-center py-4 text-gray-500">Loading links...</div>
        ) : (
          <DragDropContext onDragEnd={handleGroupsDragEnd}>
            <Droppable droppableId="link-groups" type="GROUP">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-4"
                >
                  {linkGroups.map((group, groupIndex) => (
                    <Draggable key={group.id} draggableId={group.id} index={groupIndex}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`border rounded-lg overflow-hidden ${snapshot.isDragging ? 'shadow-lg' : ''}`}
                          style={{
                            ...provided.draggableProps.style,
                            borderColor: group.color,
                            borderWidth: '2px'
                          }}
                        >
                          {/* Group Header */}
                          <div 
                            className="px-4 py-2 flex items-center gap-3"
                            style={{ backgroundColor: `${group.color}15` }}
                          >
                            <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing">
                              <GripVertical className="w-5 h-5 text-gray-400" />
                            </div>
                            
                            {editingGroupId === group.id ? (
                              <div className="flex-1 flex items-center gap-2">
                                <Input
                                  value={editGroupData.name}
                                  onChange={(e) => setEditGroupData({ ...editGroupData, name: e.target.value })}
                                  className="w-40 h-8"
                                  placeholder="Group name"
                                />
                                <Input
                                  type="color"
                                  value={editGroupData.color}
                                  onChange={(e) => setEditGroupData({ ...editGroupData, color: e.target.value })}
                                  className="w-12 h-8 p-1 cursor-pointer"
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleUpdateGroup(group.id)}
                                  className="h-8 text-green-600"
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setEditingGroupId(null)}
                                  className="h-8 text-gray-500"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                              <>
                                <div 
                                  className="w-4 h-4 rounded-full border-2"
                                  style={{ backgroundColor: group.color, borderColor: group.color }}
                                />
                                <span className="font-semibold flex-1" style={{ color: group.color }}>
                                  {group.name}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {getLinksForGroup(group.id).length} links
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => startEditGroup(group)}
                                  className="h-8"
                                >
                                  <Palette className="w-4 h-4 text-gray-500" />
                                </Button>
                                {group.id !== 'default' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteGroup(group.id)}
                                    className="h-8 text-red-500 hover:text-red-700"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                          
                          {/* Links in Group */}
                          <DragDropContext onDragEnd={(result) => handleLinksDragEnd(result, group.id)}>
                            <Droppable droppableId={`links-${group.id}`} type="LINK">
                              {(provided) => (
                                <div
                                  {...provided.droppableProps}
                                  ref={provided.innerRef}
                                  className="p-2 space-y-1 min-h-[40px] bg-white"
                                >
                                  {getLinksForGroup(group.id).map((link, linkIndex) => (
                                    <Draggable key={link.id} draggableId={link.id} index={linkIndex}>
                                      {(provided, snapshot) => (
                                        <div
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          className={`p-2 rounded border flex items-center gap-2 bg-white ${snapshot.isDragging ? 'shadow-md' : ''}`}
                                        >
                                          <div {...provided.dragHandleProps} className="cursor-grab">
                                            <GripVertical className="w-4 h-4 text-gray-300" />
                                          </div>
                                          
                                          {editingLinkId === link.id ? (
                                            <>
                                              <Input
                                                value={editLinkData.name}
                                                onChange={(e) => setEditLinkData({ ...editLinkData, name: e.target.value })}
                                                placeholder="Link name"
                                                className="w-32 h-7 text-sm"
                                              />
                                              <Input
                                                value={editLinkData.url}
                                                onChange={(e) => setEditLinkData({ ...editLinkData, url: e.target.value })}
                                                placeholder="URL"
                                                className="flex-1 h-7 text-sm"
                                              />
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleUpdateLink(link.id)}
                                                className="h-7 text-green-600"
                                              >
                                                <Check className="w-3 h-3" />
                                              </Button>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setEditingLinkId(null)}
                                                className="h-7 text-gray-500"
                                              >
                                                <X className="w-3 h-3" />
                                              </Button>
                                            </>
                                          ) : (
                                            <>
                                              <span className="font-medium text-sm" style={{ color: group.color }}>{link.name}</span>
                                              <span className="text-xs text-gray-400 truncate flex-1">{link.url}</span>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => startEditLink(link)}
                                                className="h-7"
                                              >
                                                <Edit2 className="w-3 h-3 text-gray-400" />
                                              </Button>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDeleteLink(link.id)}
                                                className="h-7 text-red-400"
                                              >
                                                <Trash2 className="w-3 h-3" />
                                              </Button>
                                            </>
                                          )}
                                        </div>
                                      )}
                                    </Draggable>
                                  ))}
                                  {provided.placeholder}
                                  {getLinksForGroup(group.id).length === 0 && (
                                    <div className="text-center py-2 text-gray-400 text-sm italic">
                                      No links in this group
                                    </div>
                                  )}
                                </div>
                              )}
                            </Droppable>
                          </DragDropContext>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>
      )} {/* end activeTab === 'quick' */}

      {/* Dashboard Apps Manager — shown under Tools tab */}
      {activeTab === 'tools' && (
      <>
      <hr className="border-gray-200" />

      {/* ===== DASHBOARD APPS SECTION ===== */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: '#1d1d1b' }}>Dashboard Apps Manager</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage the order and content of Hub page cards. Drag to reorder or add custom links.
          </p>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          style={{ backgroundColor: enconOrange }}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Custom App
        </Button>
      </div>

      {/* Apps List with Drag and Drop */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="hub-apps">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="space-y-2"
            >
              {apps.map((app, index) => (
                <Draggable key={app.id} draggableId={app.id} index={index}>
                  {(provided, snapshot) => (
                    <Card
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`p-4 ${snapshot.isDragging ? 'shadow-lg' : ''}`}
                      style={{
                        ...provided.draggableProps.style,
                        borderLeft: `4px solid ${app.color}`,
                      }}
                    >
                      <div className="flex items-center gap-4">
                        <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing">
                          <GripVertical className="w-5 h-5 text-gray-400" />
                        </div>
                        
                        <div className="flex-1 grid grid-cols-12 gap-4 items-center">
                          <div className="col-span-1 text-center font-semibold text-gray-500">
                            {index + 1}
                          </div>
                          
                          <div className="col-span-3">
                            <div className="font-semibold">{app.name}</div>
                            <div className="text-xs text-gray-500">
                              {app.is_default ? 'Default App' : 'Custom App'}
                            </div>
                          </div>
                          
                          <div className="col-span-4 text-sm text-gray-600">
                            {app.description}
                          </div>
                          
                          <div className="col-span-3 text-sm">
                            <div className="flex items-center gap-1">
                              {app.is_external && <ExternalLink className="w-3 h-3" />}
                              <span className="truncate">{app.route}</span>
                            </div>
                          </div>
                          
                          <div className="col-span-1 flex justify-end">
                            {!app.is_default && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteApp(app.id, app.is_default)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Add App Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl p-6 bg-white">
            <h3 className="text-xl font-bold mb-4">Add Custom Hub App</h3>
            
            <div className="space-y-4">
              <div>
                <Label>App Name *</Label>
                <Input
                  value={newApp.name}
                  onChange={(e) => setNewApp({ ...newApp, name: e.target.value })}
                  placeholder="e.g., Company Portal"
                />
              </div>
              
              <div>
                <Label>Description</Label>
                <Input
                  value={newApp.description}
                  onChange={(e) => setNewApp({ ...newApp, description: e.target.value })}
                  placeholder="Brief description of the app"
                />
              </div>
              
              <div>
                <Label>URL / Route *</Label>
                <Input
                  value={newApp.route}
                  onChange={(e) => setNewApp({ ...newApp, route: e.target.value })}
                  placeholder="https://example.com or /internal-page"
                />
                <div className="text-xs text-gray-500 mt-1">
                  Enter full URL for external links or route path for internal pages
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Card Color</Label>
                  <Input
                    type="color"
                    value={newApp.color}
                    onChange={(e) => setNewApp({ ...newApp, color: e.target.value })}
                  />
                </div>
                
                <div>
                  <Label>Link Type</Label>
                  <select
                    value={newApp.is_external ? 'external' : 'internal'}
                    onChange={(e) => setNewApp({ ...newApp, is_external: e.target.value === 'external' })}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="external">External URL</option>
                    <option value="internal">Internal Route</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 mt-6">
              <Button
                onClick={handleAddApp}
                style={{ backgroundColor: enconOrange }}
                className="flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save App
              </Button>
              <Button
                onClick={() => setShowAddModal(false)}
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Add Link Modal */}
      {showAddLinkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md p-6 bg-white">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <LinkIcon className="w-5 h-5" style={{ color: '#8b5cf6' }} />
              Add Quick Link
            </h3>
            
            <div className="space-y-4">
              <div>
                <Label>Link Name *</Label>
                <Input
                  value={newLink.name}
                  onChange={(e) => setNewLink({ ...newLink, name: e.target.value })}
                  placeholder="e.g., Google Drive"
                />
              </div>
              
              <div>
                <Label>URL *</Label>
                <Input
                  value={newLink.url}
                  onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                  placeholder="https://drive.google.com or /internal-page"
                />
              </div>
              
              <div>
                <Label>Group</Label>
                <select
                  value={newLink.group_id}
                  onChange={(e) => setNewLink({ ...newLink, group_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  {linkGroups.map(group => (
                    <option key={group.id} value={group.id}>{group.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex items-center gap-3 mt-6">
              <Button
                onClick={handleAddLink}
                style={{ backgroundColor: '#8b5cf6' }}
                className="flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save Link
              </Button>
              <Button
                onClick={() => { setShowAddLinkModal(false); setNewLink({ name: '', url: '', group_id: 'default' }); }}
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Add Group Modal */}
      {showAddGroupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md p-6 bg-white">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <FolderPlus className="w-5 h-5" style={{ color: '#8b5cf6' }} />
              Add Link Group
            </h3>
            
            <div className="space-y-4">
              <div>
                <Label>Group Name *</Label>
                <Input
                  value={newGroup.name}
                  onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                  placeholder="e.g., External Resources"
                />
              </div>
              
              <div>
                <Label>Group Color</Label>
                <div className="flex items-center gap-3">
                  <Input
                    type="color"
                    value={newGroup.color}
                    onChange={(e) => setNewGroup({ ...newGroup, color: e.target.value })}
                    className="w-16 h-10 p-1 cursor-pointer"
                  />
                  <span className="text-sm text-gray-500">Links in this group will use this color</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 mt-6">
              <Button
                onClick={handleAddGroup}
                style={{ backgroundColor: newGroup.color }}
                className="flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Create Group
              </Button>
              <Button
                onClick={() => { setShowAddGroupModal(false); setNewGroup({ name: '', color: '#8b5cf6' }); }}
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}
      </> 
      )} {/* end activeTab === 'tools' apps section */}

      {/* Notification Bar settings */}
      {activeTab === 'notif' && <NotificationBarSettings />}
    </div>
  );
};

export default DashboardManager;
